import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
  page: number;
  total: number;
  pageSize: number;
  onChange: (page: number) => void;
  className?: string;
}

/**
 * 通用分页组件
 *
 * 显示：上一页 · 页码（智能省略） · 下一页 · 总数
 */
export function Pagination({ page, total, pageSize, onChange, className }: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) {
    return total > 0 ? (
      <div className={`text-sm text-muted-foreground ${className ?? ""}`}>共 {total} 条</div>
    ) : null;
  }

  const goto = (p: number) => {
    if (p < 1 || p > totalPages || p === page) return;
    onChange(p);
  };

  // 智能页码：始终显示首尾页 + 当前页±1
  const pages: (number | "...")[] = [];
  const add = (n: number | "...") => {
    if (pages[pages.length - 1] !== n) pages.push(n);
  };
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 1) {
      add(i);
    } else if (i < page) {
      add("...");
    } else if (i > page) {
      add("...");
      // skip remaining middle
      i = totalPages - 1;
    }
  }

  return (
    <div className={`flex flex-wrap items-center gap-2 text-sm ${className ?? ""}`}>
      <Button
        variant="outline"
        size="sm"
        disabled={page <= 1}
        onClick={() => goto(page - 1)}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      {pages.map((p, idx) =>
        p === "..." ? (
          <span key={`dot-${idx}`} className="px-2 text-muted-foreground">…</span>
        ) : (
          <Button
            key={p}
            variant={p === page ? "default" : "outline"}
            size="sm"
            className="min-w-[36px]"
            onClick={() => goto(p)}
          >
            {p}
          </Button>
        ),
      )}
      <Button
        variant="outline"
        size="sm"
        disabled={page >= totalPages}
        onClick={() => goto(page + 1)}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      <span className="ml-2 text-muted-foreground">
        第 {page}/{totalPages} 页 · 共 {total} 条
      </span>
    </div>
  );
}
